import { useState, useMemo } from "react";
import { Building2 } from "lucide-react";

// Curated map for common names → domain
const DOMAIN_MAP: Record<string, string> = {
  netflix: "netflix.com",
  hbo: "hbo.com",
  "hbo max": "max.com",
  max: "max.com",
  disney: "disney.com",
  "disney+": "disneyplus.com",
  "disney plus": "disneyplus.com",
  hulu: "hulu.com",
  amazon: "amazon.com",
  "amazon prime": "primevideo.com",
  "prime video": "primevideo.com",
  apple: "apple.com",
  "apple tv": "tv.apple.com",
  "apple tv+": "tv.apple.com",
  paramount: "paramount.com",
  "paramount+": "paramountplus.com",
  peacock: "peacocktv.com",
  nbc: "nbc.com",
  cbs: "cbs.com",
  abc: "abc.com",
  fox: "fox.com",
  bbc: "bbc.co.uk",
  itv: "itv.com",
  channel4: "channel4.com",
  sky: "sky.com",
  showtime: "sho.com",
  starz: "starz.com",
  fx: "fxnetworks.com",
  amc: "amc.com",
  warner: "warnerbros.com",
  "warner bros": "warnerbros.com",
  "warner brothers": "warnerbros.com",
  universal: "universalpictures.com",
  sony: "sonypictures.com",
  lionsgate: "lionsgate.com",
  mgm: "mgm.com",
  a24: "a24films.com",
  pixar: "pixar.com",
  marvel: "marvel.com",
  dc: "dc.com",
  nickelodeon: "nick.com",
  "cartoon network": "cartoonnetwork.com",
  kfc: "kfc.com",
  "taco bell": "tacobell.com",
  mcdonalds: "mcdonalds.com",
  "mcdonald's": "mcdonalds.com",
  burgerking: "bk.com",
  "burger king": "bk.com",
  starbucks: "starbucks.com",
  coke: "coca-cola.com",
  "coca cola": "coca-cola.com",
  "coca-cola": "coca-cola.com",
  pepsi: "pepsi.com",
  nike: "nike.com",
  adidas: "adidas.com",
  google: "google.com",
  youtube: "youtube.com",
  meta: "meta.com",
  facebook: "facebook.com",
  instagram: "instagram.com",
  tiktok: "tiktok.com",
  spotify: "spotify.com",
};

const cache = new Map<string, string | null>();

function guessDomain(name: string): string | null {
  const key = name.trim().toLowerCase();
  if (!key) return null;
  if (DOMAIN_MAP[key]) return DOMAIN_MAP[key];
  // If user typed a domain
  if (/^[a-z0-9-]+\.[a-z]{2,}$/i.test(key)) return key;
  // Fallback: simplify to slug + .com
  const slug = key.replace(/[^a-z0-9]+/g, "");
  if (!slug) return null;
  return `${slug}.com`;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export interface CompanyLogoProps {
  name?: string | null;
  size?: number;
  className?: string;
}

export function CompanyLogo({ name, size = 18, className = "" }: CompanyLogoProps) {
  const domain = useMemo(() => (name ? guessDomain(name) : null), [name]);
  const cacheKey = domain ?? "";
  const initialSrc =
    cache.has(cacheKey) ? cache.get(cacheKey) ?? null
    : domain ? `https://logo.clearbit.com/${domain}` : null;
  const [src, setSrc] = useState<string | null>(initialSrc);

  const dim = { width: size, height: size };

  if (!name) return null;

  if (!src) {
    const init = initials(name);
    return (
      <span
        aria-hidden
        style={dim}
        className={`inline-flex items-center justify-center rounded-[4px] bg-muted text-muted-foreground text-[9px] font-semibold shrink-0 ${className}`}
        title={name}
      >
        {init || <Building2 className="size-3" />}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${name} logo`}
      loading="lazy"
      decoding="async"
      style={dim}
      className={`rounded-[4px] object-contain bg-white shrink-0 ${className}`}
      onLoad={() => cache.set(cacheKey, src)}
      onError={() => {
        cache.set(cacheKey, null);
        setSrc(null);
      }}
    />
  );
}
